import React, { useMemo, useState } from "react";
import {
  Select,
  MyMessage,
  InputField,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneParty } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import {
  ContactSection,
  SocialSection,
  GeoSection,
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import {
  FiImage,
  FiClock,
  FiDollarSign,
  FiRepeat,
} from "react-icons/fi";
import { FaGlassCheers } from "react-icons/fa";

const toSlug = (s: any) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const toList = (s: any) =>
  (s || "")
    .split("|")
    .map((x: string) => x.trim())
    .filter(Boolean);

const toStringRepeat = (arr: string[]) => (arr?.length ? arr.join("|") : null);

const REPEAT_OPTIONS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "annual",
];

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const party: any = getOneParty();

  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const party_id = party?.party_id;

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [title, setTitle] = useState<any>(party?.title || "");
  const [thumbnail, setThumbnail] = useState<any>(
    party?.thumbnail || defaultImage
  );
  const [thumbnailId, setThumbnailId] = useState<any>(
    party?.image_cdn_id || "img_cdn_id"
  );

  const [venue, setVenue] = useState<any>(party?.venue || "");
  const [venueCapacity, setVenueCapacity] = useState<any>(
    party?.venue_capacity || ""
  );
  const [startDate, setStartDate] = useState<any>(party?.start_date || "");
  const [endDate, setEndDate] = useState<any>(party?.end_date || "");
  const [startTime, setStartTime] = useState<any>(party?.start_time || "");
  const [endTime, setEndTime] = useState<any>(party?.end_time || "");

  const [email, setEmail] = useState<any>(party?.email_address || "");
  const [payLink, setPayLink] = useState<any>(party?.pay_link);
  const [phoneNumber, setPhoneNumber] = useState<any>(party?.phone_number || "");
  const [whatsapp, setWhatsapp] = useState<any>(party?.whatsapp || "none");
  const [facebook, setFacebook] = useState<any>(party?.facebook || "none");
  const [twitter, setTwitter] = useState<any>(party?.twitter || "none");
  const [instagram, setInstagram] = useState<any>(party?.instagram || "none");
  const [youtube, setYoutube] = useState<any>(party?.youtube || "none");
  const [hashtags, setHashtags] = useState<any>(party?.hashtag || "");

  const [googleMapCode, setGoogleMapCode] = useState<any>(
    party?.google_map_code || "none"
  );
  const [latitude, setLatitude] = useState<any>(party?.latitude || "none");
  const [longitude, setLongitude] = useState<any>(party?.longitude || "none");
  const [formattedAddress, setFormattedAddress] = useState<any>(
    party?.formatted_address || ""
  );

  const [featured, setFeatured] = useState<any>(party?.featured ?? "no");
  const [promoted, setPromoted] = useState<any>(party?.promoted ?? "no");
  const [partyStatus, setPartyStatus] = useState<any>(party?.status ?? 1);
  const [type, setType] = useState<any>(party?.type || "public");
  const [expo, setExpo] = useState<any>(party?.expo ?? 0);
  const [showTop, setShowTop] = useState<any>(party?.show_top ?? 0);

  const [repeatEveryList, setRepeatEveryList] = useState<string[]>(
    toList(party?.repeat_every)
  );
  const [description, setDescription] = useState<any>(party?.description || "");
  const [pricing, setPricing] = useState<any>(party?.Pricings || []);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(false);

  const repeatEvery = useMemo(
    () => toStringRepeat(repeatEveryList),
    [repeatEveryList]
  );

  const partyRoutes = "/data/party";

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  const validate = () => {
    if (!title?.trim()) return "Title is required.";
    if (!thumbnail || !thumbnailId) return "Image is required.";
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return "End date cannot be before start date.";
    return null;
  };

  const updateOnePartyHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const err = validate();
    if (err) {
      responses(true, err);
      return;
    }
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : party?.country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title: title?.trim(),
      slug: title ? toSlug(title) : null,
      description,

      venue: venue || null,
      venue_capacity: venueCapacity || null,
      start_date: startDate || null,
      end_date: endDate || null,
      start_time: startTime || null,
      end_time: endTime || null,

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || null,
      email_address: email || null,
      pay_link: payLink,
      whatsapp: whatsapp || "none",
      facebook: facebook || "none",
      twitter: twitter || "none",
      instagram: instagram || "none",
      youtube: youtube || "none",
      hashtag: hashtags || "",

      audio_adv: "none",
      google_map_code: googleMapCode || "none",
      latitude: latitude || "none",
      longitude: longitude || "none",
      formatted_address: formattedAddress || null,
      country_short: "none",

      status: partyStatus,
      featured,
      promoted,
      type,
      repeat_every: repeatEvery,
      expo,
      show_top: showTop,
      pricing,
    };

    const { message, status } = await updateOneContent({
      data_id: party_id,
      data,
      content: _TYPES_.PARTY,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const toggleRepeat = (val: string) => {
    setRepeatEveryList((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  const chipsBase =
    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs cursor-pointer select-none transition-colors";
  const chipOn = "border-brand bg-brand/10 text-brand";
  const chipOff =
    "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50";

  return (
    <AddPageShell
      backRoute={partyRoutes}
      eyebrow="DoSee Data • Parties"
      title="Edit Party"
      subtitle={`Update a party experience in ${entry_label || "this destination"}.`}
      icon={<FaGlassCheers className="h-5 w-5" />}
      pills={type === "private" ? [{ label: "Private" }] : undefined}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOnePartyHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage("");
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="The party flyer or hero image."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.PARTY)}
        />
      </AddSection>

      <AddSection title="Party Details" hint="Title, venue and party type.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title,
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "top",
              title: "Show In Top Parties",
              option: ["no", "yes"],
              preselect:
                showTop !== undefined && showTop !== null
                  ? [{ name: showTop === 0 ? "no" : "yes" }]
                  : null,
              onChange: (e: any) =>
                setShowTop(e.target.selectedIndex === 0 ? 0 : 1),
            }}
          />
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-gray-600">Type</p>
            <div className="inline-flex w-fit rounded-lg border border-gray-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setType("public")}
                className={`rounded-md px-3 py-1.5 text-sm transition ${
                  type === "public"
                    ? "bg-gradient-to-br from-brand to-primary text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Public
              </button>
              <button
                type="button"
                onClick={() => setType("private")}
                className={`rounded-md px-3 py-1.5 text-sm transition ${
                  type === "private"
                    ? "bg-gradient-to-br from-brand to-primary text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Private
              </button>
            </div>
          </div>
          <InputField
            props={{
              id: "venue",
              title: "Venue",
              type: "text",
              value: venue,
              onChange: (e: any) => {
                setVenue(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "venue_capacity",
              title: "Venue Capacity",
              type: "number",
              value: venueCapacity,
              onChange: (e: any) => {
                setVenueCapacity(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress,
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Date & Time"
        hint="When the party happens."
        icon={<FiClock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <InputField
            props={{
              id: "start_date",
              title: "Start Date",
              type: "date",
              value: startDate,
              onChange: (e: any) => setStartDate(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "end_date",
              title: "End Date",
              type: "date",
              value: endDate,
              onChange: (e: any) => setEndDate(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "start_time",
              title: "Start Time",
              type: "time",
              value: startTime,
              onChange: (e: any) => setStartTime(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "end_time",
              title: "End Time",
              type: "time",
              value: endTime,
              onChange: (e: any) => setEndTime(e.target.value),
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Repeat Schedule"
        hint="Optional — recurring days or annual."
        icon={<FiRepeat className="h-4 w-4" />}
      >
        <div className="flex flex-wrap gap-2">
          {REPEAT_OPTIONS.map((opt) => {
            const on = repeatEveryList.includes(opt);
            return (
              <span
                key={opt}
                role="button"
                tabIndex={0}
                onClick={() => toggleRepeat(opt)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggleRepeat(opt)
                }
                className={`${chipsBase} ${on ? chipOn : chipOff} capitalize`}
              >
                {on ? (
                  <MdCheckBox className="text-base" />
                ) : (
                  <MdCheckBoxOutlineBlank className="text-base" />
                )}
                {opt}
              </span>
            );
          })}
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <ContactSection
        phone={phoneNumber}
        setPhone={setPhoneNumber}
        email={email}
        setEmail={setEmail}
        payLink={payLink}
        setPayLink={setPayLink}
        clearMessages={clearMessages}
      />

      <SocialSection
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        facebook={facebook}
        setFacebook={setFacebook}
        twitter={twitter}
        setTwitter={setTwitter}
        instagram={instagram}
        setInstagram={setInstagram}
        youtube={youtube}
        setYoutube={setYoutube}
        clearMessages={clearMessages}
      />

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <VisibilitySection
        statusId="party_status"
        setStatus={setPartyStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        status={partyStatus}
        featured={featured}
        promoted={promoted}
        expo={expo}
        clearMessages={clearMessages}
      />

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
